import { forwardRef, useEffect, useMemo, useRef } from "react";
import { usePaperBlocksQuery, usePaperPagesQuery } from "../api/queries";
import type { BlockInfo, PageImage } from "../types";
import { scrollToTarget } from "../utils/scrollToTarget";

interface PageWithBlocks {
  pageNum: number;
  blocks: BlockInfo[];
}

interface PageDimensions {
  width: number;
  height: number;
}

interface ArxivPdfViewerProps {
  docId: string;
  highlights: Set<number>;
}

export const ArxivPdfViewer = ({ docId, highlights }: ArxivPdfViewerProps) => {
  const firstPageRef = useRef<HTMLDivElement>(null);
  const lineNums = useMemo(
    () => [...highlights].sort((a, b) => a - b),
    [highlights],
  );

  const {
    data: blocksData,
    isPending: blocksLoading,
    error: blocksError,
  } = usePaperBlocksQuery(docId, lineNums);

  const pageDims: PageDimensions | null = blocksData?.page_dimensions ?? null;
  const error =
    blocksError instanceof Error
      ? blocksError.message
      : blocksError
        ? String(blocksError)
        : null;

  const pageGroups: PageWithBlocks[] = useMemo(() => {
    const map = new Map<number, BlockInfo[]>();
    for (const block of blocksData?.blocks ?? []) {
      if (!block.page) continue;
      const existing = map.get(block.page) || [];
      existing.push(block);
      map.set(block.page, existing);
    }
    return [...map.entries()]
      .sort(([a], [b]) => a - b)
      .map(([pageNum, pageBlocks]) => ({ pageNum, blocks: pageBlocks }));
  }, [blocksData?.blocks]);

  const pageNums = useMemo(
    () => pageGroups.map((g) => g.pageNum),
    [pageGroups],
  );
  const { pageImages } = usePaperPagesQuery(docId, pageNums);

  useEffect(() => {
    if (pageImages.size > 0 && firstPageRef.current) {
      const container = firstPageRef.current.closest<HTMLElement>("[data-scroll-container]")
        ?? firstPageRef.current.closest<HTMLElement>(".overflow-y-auto");
      setTimeout(() => {
        scrollToTarget(container, firstPageRef.current);
      }, 100);
    }
  }, [pageImages.size]);

  if (lineNums.length === 0 || blocksLoading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-zinc-400 text-sm">
          {blocksLoading ? "Loading PDF…" : "No page/bbox data available for these citations."}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-red-500 text-sm" role="alert">{error}</div>
      </div>
    );
  }

  if (pageGroups.length === 0) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="text-zinc-400 text-sm">
          No page/bbox data available for these citations.
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {pageGroups.map((group, idx) => (
        <PdfPage
          key={group.pageNum}
          ref={idx === 0 ? firstPageRef : undefined}
          pageNum={group.pageNum}
          pageImg={pageImages.get(group.pageNum)}
          blocks={group.blocks}
          pageDims={pageDims}
        />
      ))}
    </div>
  );
};

interface PdfPageProps {
  pageNum: number;
  pageImg: PageImage | undefined;
  blocks: BlockInfo[];
  pageDims: PageDimensions | null;
}

const PdfPage = forwardRef<HTMLDivElement, PdfPageProps>(
  ({ pageNum, pageImg, blocks, pageDims }, ref) => (
    <div
      ref={ref}
      className="rounded-[3px] overflow-hidden border border-zinc-200 shadow-sm"
    >
      <div className="bg-zinc-50 px-4 py-2 border-b border-zinc-200 flex items-center justify-between">
        <span className="text-xs font-medium text-zinc-600">
          Page {pageNum}
        </span>
        {blocks.length > 0 && (
          <span className="text-xs text-zinc-400">
            {blocks.length} cited block{blocks.length !== 1 ? "s" : ""}
          </span>
        )}
      </div>
      <div className="bg-zinc-700 p-4 flex justify-center">
        {pageImg ? (
          <div className="relative inline-block shadow-xl">
            <img
              src={`data:image/png;base64,${pageImg.image_base64}`}
              alt={`Page ${pageNum}`}
              className="max-w-full h-auto"
            />
            {blocks.map((block, bIdx) => {
              if (!block.bbox) return null;
              const [x1, y1, x2, y2] = block.bbox;
              const pageW = pageDims?.width || pageImg.width;
              const pageH = pageDims?.height || pageImg.height;
              const left = (x1 / pageW) * 100;
              const top = (y1 / pageH) * 100;
              const width = ((x2 - x1) / pageW) * 100;
              const height = ((y2 - y1) / pageH) * 100;
              return (
                <div
                  key={bIdx}
                  className="absolute border-2 border-amber-400 bg-amber-300/20 pointer-events-none rounded-sm"
                  style={{
                    left: `${left}%`,
                    top: `${top}%`,
                    width: `${width}%`,
                    height: `${height}%`,
                  }}
                />
              );
            })}
          </div>
        ) : (
          <div className="flex items-center justify-center h-48">
            <div className="text-zinc-400 text-sm">Loading page…</div>
          </div>
        )}
      </div>
    </div>
  )
);

PdfPage.displayName = "PdfPage";
